import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "sonner";

import AuthPage from "@/pages/AuthPage";
import Layout from "@/components/Layout";
import Dashboard from "@/pages/Dashboard";
import HookGenerator from "@/pages/HookGenerator";
import Remix from "@/pages/Remix";
import Score from "@/pages/Score";
import Library from "@/pages/Library";
import Pricing from "@/pages/Pricing";
import Referrals from "@/pages/Referrals";
import AdminPanel from "@/pages/AdminPanel";
import Support from "@/pages/Support";
import Wall from "@/pages/Wall";
import BrandVoice from "@/pages/BrandVoice";
import Calendar from "@/pages/Calendar";
import CreatorProfile from "@/pages/CreatorProfile";
import { PaymentSuccess, PaymentCancel } from "@/pages/Payment";

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <div className="font-mono text-xs text-zinc-500 tracking-[0.25em] uppercase animate-pulse">// initializing engine</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/auth" replace />;
  return <Layout>{children}</Layout>;
}

function AtHandleGate() {
  const location = useLocation();
  const raw = decodeURIComponent(location.pathname);
  const match = raw.match(/^\/@([a-zA-Z0-9_-]+)\/?$/);
  if (match) return <CreatorProfile handleOverride={match[1]} />;
  return <Navigate to="/" replace />;
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster
            position="bottom-right"
            theme="dark"
            toastOptions={{
              style: {
                background: "#141414",
                border: "1px solid rgba(255,255,255,0.15)",
                color: "#fff",
                borderRadius: 0,
                fontFamily: "JetBrains Mono, monospace",
                fontSize: 12,
              },
            }}
          />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/app" element={<Protected><Dashboard /></Protected>} />
            <Route path="/app/hooks" element={<Protected><HookGenerator /></Protected>} />
            <Route path="/app/remix" element={<Protected><Remix /></Protected>} />
            <Route path="/app/score" element={<Protected><Score /></Protected>} />
            <Route path="/app/library" element={<Protected><Library /></Protected>} />
            <Route path="/app/pricing" element={<Protected><Pricing /></Protected>} />
            <Route path="/app/referrals" element={<Protected><Referrals /></Protected>} />
            <Route path="/app/admin" element={<Protected><AdminPanel /></Protected>} />
            <Route path="/app/brand-voice" element={<Protected><BrandVoice /></Protected>} />
            <Route path="/app/calendar" element={<Protected><Calendar /></Protected>} />
            <Route path="/creator/:handle" element={<CreatorProfile />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/support" element={<Support />} />
            <Route path="/wall" element={<Wall />} />
            <Route path="*" element={<AtHandleGate />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

function Landing() {
  return (
    <div className="min-h-screen bg-void text-white">
      <header className="px-6 md:px-12 py-6 flex items-center justify-between border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-signal-yellow text-black flex items-center justify-center font-mono font-bold">V</div>
          <div>
            <div className="heading-display text-lg leading-none">VIRAL/CO</div>
            <div className="font-mono text-[10px] text-zinc-500 tracking-[0.25em] mt-1">CONTENT MACHINE</div>
          </div>
        </div>
        <a href="/auth" data-testid="landing-cta-top" className="bg-signal-yellow text-black font-bold uppercase tracking-[0.18em] text-xs px-5 py-2.5 hover:bg-signal-yellow-hover transition-colors">
          Start free →
        </a>
      </header>

      <section className="px-6 md:px-12 lg:px-20 py-16 md:py-24 grid lg:grid-cols-12 gap-8 items-end border-b border-white/10">
        <div className="lg:col-span-8 space-y-6">
          <div className="label-eyebrow">// engineered for creators who don&apos;t have time to lose</div>
          <h1 className="heading-display text-5xl sm:text-7xl lg:text-8xl leading-[0.9]">
            VIRAL CONTENT.<br />
            <span className="text-signal-yellow">ON DEMAND.</span>
          </h1>
          <p className="font-sans text-lg text-zinc-400 max-w-2xl leading-relaxed">
            A brutally efficient machine for TikTok, Reels, Shorts, Twitter/X and LinkedIn. Generate hooks, remix tired posts, score your drafts — powered by Claude Sonnet 4.5.
          </p>
          <div className="flex gap-3 flex-wrap">
            <a href="/auth" data-testid="landing-cta-main" className="bg-signal-yellow text-black font-bold uppercase tracking-[0.18em] text-xs px-6 py-4 hover:bg-signal-yellow-hover transition-colors">
              Start the engine →
            </a>
            <a href="#features" className="border border-white/20 text-white font-mono uppercase tracking-[0.18em] text-xs px-6 py-4 hover:border-white/60 transition-colors">
              See modules
            </a>
          </div>
        </div>
        <div className="lg:col-span-4 brutal-border bg-[#141414] p-6 space-y-3">
          <div className="label-eyebrow">// live status</div>
          <div className="space-y-2 font-mono text-xs text-zinc-400">
            <div className="flex justify-between"><span>engine</span><span className="text-signal-yellow">ONLINE</span></div>
            <div className="flex justify-between"><span>model</span><span>claude-sonnet-4.5</span></div>
            <div className="flex justify-between"><span>latency</span><span>~ 220ms</span></div>
            <div className="flex justify-between"><span>uptime</span><span>99.99%</span></div>
            <div className="flex justify-between"><span>build</span><span>2026.02</span></div>
          </div>
        </div>
      </section>

      <section id="features" className="px-6 md:px-12 lg:px-20 py-16 md:py-24 space-y-10 border-b border-white/10">
        <div className="label-eyebrow">// three modules. one workflow.</div>
        <div className="grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
          {[
            { n: "01", t: "HOOK FORGE", d: "Drop a topic. Get 5 scroll-stopping openers engineered for the platform of your choice." },
            { n: "02", t: "REMIX LAB", d: "Paste an existing post. Receive a viral rewrite — new hook, new rhythm, new CTA." },
            { n: "03", t: "SCORE ENGINE", d: "Draft a post. Get a 0–100 virality score and a brutally honest list of fixes." },
          ].map((f) => (
            <div key={f.n} className="bg-void p-8 space-y-4 hover:bg-[#141414] transition-colors">
              <div className="font-mono text-xs text-signal-yellow tracking-[0.25em]">{f.n}</div>
              <h3 className="heading-display text-2xl">{f.t}</h3>
              <p className="font-sans text-sm text-zinc-400 leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-20 py-16 md:py-20 flex flex-col md:flex-row justify-between items-start md:items-end gap-6">
        <div>
          <h2 className="heading-display text-3xl sm:text-5xl max-w-xl">STOP WAITING. START SHIPPING.</h2>
          <p className="font-mono text-xs text-zinc-500 mt-3 tracking-wider uppercase">// free to start · no card · 30 seconds to first hook</p>
        </div>
        <a href="/auth" data-testid="landing-cta-bottom" className="bg-signal-yellow text-black font-bold uppercase tracking-[0.18em] text-xs px-6 py-4 hover:bg-signal-yellow-hover transition-colors">
          Create account →
        </a>
      </section>

      <footer className="px-6 md:px-12 lg:px-20 py-8 border-t border-white/10 font-mono text-[10px] text-zinc-600 tracking-[0.2em] uppercase flex flex-col sm:flex-row justify-between gap-3">
        <span>© VIRAL/CO — viral-co-hq.com — 2026</span>
        <div className="flex gap-4">
          <a href="/wall" data-testid="footer-wall" className="hover:text-signal-yellow transition-colors">Wall of 90+</a>
          <a href="/support" data-testid="footer-support" className="hover:text-signal-yellow transition-colors">Support</a>
          <a href="/app/pricing" className="hover:text-signal-yellow transition-colors">Pricing</a>
          <span>build_v1.0</span>
        </div>
      </footer>
    </div>
  );
}
